import { type Organization_userProfile$key } from '#app/__generated__/relay/Organization_userProfile.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { Link } from '#app/components/Link/index.js';
import { OrganizationLogoEmptyState } from '#app/components/OrganizationLogoEmptyState/OrganizationLogoEmptyState.js';
import { Text } from '#app/components/Primitives/Text.js';
import { formatUrlToDomainAndPath } from './utils/formatUrlToDomainAndPath.js';
import { EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { lazy, Suspense, useState } from 'react';
import { css, styled } from 'styled-components';

const EditHiringDetailsModal = lazy(async () => {
  return {
    default: (
      await import('./EditHiringDetailsModal/EditHiringDetailsModal.js')
    ).EditHiringDetailsModal,
  };
});

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
`;

const CompanyDetails = styled.div`
  > * + * {
    margin-top: 4px;
  }

  strong {
    font-weight: 600;
  }
`;

const Url = styled(Text)`
  ${({ theme }) => css`
    word-break: break-all;
    color: ${theme.colorsV2.gray70};
  `}
`;

export const Organization = ({
  disableEditing = false,
  openLinkInNewWindow,
  userProfileRef,
}: {
  readonly disableEditing?: boolean;
  readonly openLinkInNewWindow?: boolean;
  readonly userProfileRef: Organization_userProfile$key;
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);

  const profile = useFragment<Organization_userProfile$key>(
    graphql`
      fragment Organization_userProfile on UserProfile {
        visitorCanEdit
        professionalTitle
        organization {
          description
          id
          logoImage {
            id
            uid
            url
          }
          name
          url
          slug
        }
      }
    `,
    userProfileRef,
  );

  if (!profile?.organization) return null;

  const { visitorCanEdit } = profile;
  const { logoImage, name, slug, url } = profile.organization;

  const companyDetails = (
    <CompanyDetails>
      <Text>
        {profile.professionalTitle ?? 'Hiring Contact'} at{' '}
        <strong>{name}</strong>
      </Text>
      {url ? (
        <Url textStyle="bodySmall">{formatUrlToDomainAndPath(url)}</Url>
      ) : null}
    </CompanyDetails>
  );

  if (visitorCanEdit && !disableEditing) {
    return (
      <>
        <div style={{ textAlign: 'left', width: '100%' }}>
          <InlineEditContainer
            label="Edit your company"
            onClick={() => {
              setEditModalOpen(true);
            }}
            visitorCanEdit={visitorCanEdit}
          >
            <Container>
              {logoImage ? (
                <Avatar
                  avatarImage={logoImage}
                  size="md"
                />
              ) : (
                <OrganizationLogoEmptyState size="md" />
              )}
              {companyDetails}
            </Container>
            <EditIcon size={16} />
          </InlineEditContainer>
        </div>
        <Suspense>
          {editModalOpen ? (
            <EditHiringDetailsModal
              initialData={{
                company: profile.organization
                  ? {
                      description: profile.organization.description,
                      id: profile.organization.id,
                      isClaimed: false,
                      location: null,
                      logoImage: profile.organization.logoImage
                        ? {
                            id: profile.organization.logoImage.id,
                            uid: profile.organization.logoImage.uid,
                          }
                        : null,
                      name: profile.organization.name,
                      slug: profile.organization.slug,
                      url: profile.organization.url,
                      visitorCanClaim: false,
                    }
                  : null,
                isHiringAsIndividual: profile.organization === null,
                professionalTitle: profile.professionalTitle,
              }}
              isOpen={editModalOpen}
              onClose={() => {
                setEditModalOpen(false);
              }}
            />
          ) : null}
        </Suspense>
      </>
    );
  }

  return (
    <Container
      aria-label={`to ${name} company profile`}
      as={slug ? Link : 'div'}
      to={
        slug
          ? getRoute('/company/@slug', {
              args: {
                slug,
              },
            })
          : undefined
      }
      {...(openLinkInNewWindow ? { target: '_blank' } : {})}
    >
      {logoImage ? (
        <Avatar
          avatarImage={logoImage}
          size="md"
        />
      ) : (
        <OrganizationLogoEmptyState size="md" />
      )}
      {companyDetails}
    </Container>
  );
};
